import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { FaArrowLeft, FaBullhorn, FaCrown, FaShieldAlt, FaSignOutAlt, FaUserCheck, FaUserSlash } from 'react-icons/fa';
import Seo from '../../components/Seo';
import { useAuth } from '../../contexts/AuthContext';
import {
  approveRequest, cancelJoinRequest, createBoardPost, denyRequest, getProfilesByIds, joinOrRequestTeam,
  leaveTeam, listenTeam, listenTeamBoard, listenTeamMembers, listenTeamRequests, removeMember, setMemberRole,
} from '../../services/teamService';
import { TEAM_MEMBER_STATUS, TEAM_ROLE, text } from '../../contracts/p4r';
import './TeamDetailPage.css';

export default function TeamDetailPage() {
  const { teamId } = useParams();
  const navigate = useNavigate();
  const { user, profile, profileComplete } = useAuth();
  const [team, setTeam] = useState(null);
  const [members, setMembers] = useState([]);
  const [requests, setRequests] = useState([]);
  const [board, setBoard] = useState([]);
  const [profiles, setProfiles] = useState({});
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [busy, setBusy] = useState('');
  const [announcement, setAnnouncement] = useState({ body: '', category: '' });

  useEffect(() => listenTeam(teamId, setTeam, (err) => setError(err.message)), [teamId]);
  useEffect(() => {
    if (!user) return undefined;
    const unsubs = [
      listenTeamMembers(teamId, setMembers, (err) => setError(err.message)),
      listenTeamRequests(teamId, setRequests, () => setRequests([])),
      listenTeamBoard(teamId, setBoard, () => setBoard([])),
    ];
    return () => unsubs.forEach((unsubscribe) => unsubscribe?.());
  }, [teamId, user]);

  useEffect(() => {
    const ids = [...members.map((item) => item.uid || item.id), ...requests.map((item) => item.uid || item.id)];
    if (!ids.length) return;
    getProfilesByIds(ids).then(setProfiles).catch(() => {});
  }, [members, requests]);

  const membership = useMemo(() => members.find((item) => (item.uid || item.id) === user?.uid), [members, user?.uid]);
  const isActiveMember = membership?.status === TEAM_MEMBER_STATUS.ACTIVE;
  const isOwner = team?.ownerId === user?.uid;
  const isModerator = isOwner || team?.admins?.includes(user?.uid);
  const pendingRequest = requests.some((item) => (item.uid || item.id) === user?.uid && item.status === 'PENDING');
  const activeMembers = members.filter((item) => item.status === TEAM_MEMBER_STATUS.ACTIVE);

  const execute = async (key, action, success) => {
    setBusy(key); setError(''); setNotice('');
    try { await action(); setNotice(success || 'Done.'); } catch (err) { setError(err.message || 'Action failed.'); }
    finally { setBusy(''); }
  };

  const join = () => execute('join', async () => {
    if (!user) return navigate('/login');
    if (!profileComplete) throw new Error('Complete your player profile first. Add a nickname, bio, and profile photo.');
    const result = await joinOrRequestTeam(teamId, user.uid);
    setNotice(result === 'REQUESTED' ? 'Join request sent.' : result === 'JOINED' ? 'You joined the team.' : 'You are already a member.');
  });

  const leave = () => {
    if (!window.confirm('Leave this team? You will lose access to its private board and may need to request or join again later.')) return;
    execute('leave', () => leaveTeam(teamId, user.uid), 'You left the team.');
  };

  const memberName = (uid) => text(profiles[uid]?.nickname || profiles[uid]?.name || uid);

  if (!team) return <main className="p4r-detail-page"><div className="p4r-detail-shell">Loading team...</div></main>;

  return (
    <main className="p4r-detail-page">
      <Seo
        title={`${team.name} | Pool Team — Pool 4 Real`}
        description={`${team.bio || `View ${team.name}, a pool team on Pool 4 Real.`}`}
        path={`/teams/${teamId}`}
      />
      <div className="p4r-detail-shell">
        <Link className="p4r-detail-back" to="/bucks"><FaArrowLeft /> Teams House</Link>
        <section className="p4r-team-hero" style={team.bannerUrl ? { backgroundImage: `linear-gradient(rgba(7,10,16,.6),rgba(7,10,16,.95)),url(${team.bannerUrl})` } : undefined}>
          <p>{team.visibility} • {team.joinPolicy === 'APPROVAL_REQUIRED' ? 'Approval required' : 'Open joining'}</p>
          <h1>{team.name}</h1>
          <div>{team.bio}</div>
          <span>{team.membersCount || activeMembers.length} members {team.primaryVenue ? `• ${team.primaryVenue}` : ''}</span>
          <div className="p4r-team-actions">
            {!isActiveMember && !pendingRequest && <button onClick={join} disabled={busy === 'join'}>{busy === 'join' ? 'Working...' : team.joinPolicy === 'APPROVAL_REQUIRED' ? 'Request to join' : 'Join team'}</button>}
            {pendingRequest && <button className="secondary" onClick={() => execute('cancel-request', () => cancelJoinRequest(teamId, user.uid), 'Request cancelled.')}>Cancel request</button>}
            {isActiveMember && !isOwner && <button className="danger" onClick={leave}><FaSignOutAlt /> Leave team</button>}
          </div>
        </section>

        {error && <div className="p4r-detail-alert error">{error}</div>}
        {notice && <div className="p4r-detail-alert success">{notice}</div>}

        <div className="p4r-detail-grid">
          <section className="p4r-detail-card">
            <h2>Roster</h2>
            {activeMembers.length === 0 ? <p>No active roster members.</p> : activeMembers.map((member) => {
              const uid = member.uid || member.id;
              const canManage = isModerator && uid !== team.ownerId && uid !== user?.uid;
              return (
                <article className="p4r-roster-row" key={uid}>
                  <div>
                    <strong>{memberName(uid)}</strong>
                    <span>{member.role === TEAM_ROLE.OWNER ? <><FaCrown /> Owner</> : member.role === TEAM_ROLE.ADMIN ? <><FaShieldAlt /> Admin</> : 'Member'}</span>
                  </div>
                  {canManage && <div className="p4r-inline-actions">
                    {isOwner && <button onClick={() => execute(`role-${uid}`, () => setMemberRole(teamId, uid, member.role === TEAM_ROLE.ADMIN ? TEAM_ROLE.MEMBER : TEAM_ROLE.ADMIN, user.uid), 'Role updated.')}>{member.role === TEAM_ROLE.ADMIN ? 'Demote' : 'Make admin'}</button>}
                    <button onClick={() => window.confirm(`Remove ${memberName(uid)} from this team?`) && execute(`remove-${uid}`, () => removeMember(teamId, uid, user.uid, false), 'Member removed.')}><FaUserSlash /> Remove</button>
                    <button className="danger" onClick={() => window.confirm(`Ban ${memberName(uid)}? They will lose their roster spot and cannot rejoin unless the ban is removed.`) && execute(`ban-${uid}`, () => removeMember(teamId, uid, user.uid, true), 'Member banned.')}>Ban</button>
                  </div>}
                </article>
              );
            })}
          </section>

          <section className="p4r-detail-card">
            <h2>Team board</h2>
            {!user && <p>Sign in to access team announcements.</p>}
            {user && !isActiveMember && !isModerator && <p>Join the active roster to access the team board.</p>}
            {(isActiveMember || isModerator) && <>
              {isModerator && <form className="p4r-board-form" onSubmit={(event) => {
                event.preventDefault();
                execute('announcement', () => createBoardPost(teamId, user.uid, profile?.nickname || user.displayName, announcement.body, announcement.category), 'Announcement published.');
                setAnnouncement({ body: '', category: '' });
              }}>
                <input placeholder="Optional category" value={announcement.category} onChange={(e) => setAnnouncement((prev) => ({ ...prev, category: e.target.value }))} maxLength={40} />
                <textarea placeholder="Share an announcement with the team" value={announcement.body} onChange={(e) => setAnnouncement((prev) => ({ ...prev, body: e.target.value }))} maxLength={500} required />
                <button disabled={busy === 'announcement'}><FaBullhorn /> Publish</button>
              </form>}
              <div className="p4r-board-list">
                {board.length === 0 ? <p>No team announcements yet.</p> : board.map((post) => <article key={post.id}><small>{post.category || 'Announcement'}</small><p>{post.body}</p><span>{post.createdByName || 'Team moderator'}</span></article>)}
              </div>
            </>}
          </section>
        </div>

        {isModerator && <section className="p4r-detail-card p4r-requests-card">
          <h2>Pending requests</h2>
          {requests.filter((item) => item.status === 'PENDING').length === 0 ? <p>No pending requests.</p> : requests.filter((item) => item.status === 'PENDING').map((request) => {
            const uid = request.uid || request.id;
            return <article className="p4r-request-row" key={uid}><strong>{memberName(uid)}</strong><div><button onClick={() => execute(`approve-${uid}`, () => approveRequest(teamId, uid, user.uid), 'Request approved.')}><FaUserCheck /> Approve</button><button className="secondary" onClick={() => execute(`deny-${uid}`, () => denyRequest(teamId, uid, user.uid), 'Request denied.')}>Deny</button></div></article>;
          })}
        </section>}
      </div>
    </main>
  );
}
